"use client";

import Image from "next/image";
import Link from "next/link";
import { Handshake } from "lucide-react";
import { usePathname } from "next/navigation";

import { SectionReveal } from "@/components/ui/section-reveal";
import { SocialLinks } from "@/components/site/layout/social-links";
import { footerNavGroups } from "@/lib/content/navigation";
import { cn } from "@/lib/utils";

const HOME_PATH = "/";

function normalizeHref(href: string) {
  const [rawPath] = href.split("#");
  return rawPath || HOME_PATH;
}

function matchesRoute(pathname: string, hrefPath: string) {
  if (hrefPath === HOME_PATH) {
    return pathname === HOME_PATH;
  }

  return pathname === hrefPath || pathname.startsWith(`${hrefPath}/`);
}

export function SiteFooter() {
  const pathname = usePathname();
  const hasHomeFloatingCta = pathname === "/";
  const currentYear = new Date().getFullYear();

  const isHrefActive = (href: string) => matchesRoute(pathname, normalizeHref(href));

  return (
    <footer
      className={cn(
        "pt-16 pb-16 md:pt-20 md:pb-20",
        hasHomeFloatingCta && "md:pb-[7rem]",
      )}
    >
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-12">
        <div className="relative pt-10 md:pt-12">
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border/70 to-transparent"
          />
          <div
            aria-hidden
            className="absolute left-1/2 top-0 h-px w-28 -translate-x-1/2 bg-foreground/20"
          />

          <div className="grid gap-12 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] lg:items-start lg:gap-12">
            <SectionReveal className="max-w-[18rem] space-y-5">
              <Link href="/" aria-label="Cooperly home" className="inline-flex">
                <Image
                  src="/cooperly/cooperly_logo.svg"
                  alt="Cooperly logo"
                  width={120}
                  height={28}
                  className="h-auto w-[120px]"
                />
              </Link>
              <p className="text-pretty text-base leading-7 text-muted-foreground">
                Cooperly helps founder-led teams scale shared people context
                without adding management bureaucracy.
              </p>
              <SocialLinks />
              <p className="text-sm leading-6 text-muted-foreground">
                © {currentYear} Cooperly. All rights reserved.
              </p>
            </SectionReveal>

            <div className="grid gap-x-10 gap-y-10 sm:grid-cols-2 xl:grid-cols-4 xl:gap-x-12">
              {footerNavGroups.map((section, index) => {
                return (
                  <SectionReveal
                    key={section.label}
                    delay={0.08 + index * 0.06}
                    className="space-y-4"
                  >
                    <h3 className="site-eyebrow font-semibold text-foreground">
                      {"href" in section ? (
                        <Link
                          href={section.href}
                          className="inline-flex w-fit border-b border-transparent pb-0.5 transition-colors duration-200 hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
                        >
                          {section.label}
                        </Link>
                      ) : (
                        section.label
                      )}
                    </h3>
                    <ul className="space-y-2.5 text-sm leading-6 text-muted-foreground">
                      {section.links.map((link) => (
                        <FooterLinkItem
                          key={`${section.label}-${link.title}`}
                          link={link}
                          isHrefActive={isHrefActive}
                        />
                      ))}
                    </ul>
                  </SectionReveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLinkItem({
  link,
  isHrefActive,
}: {
  link: { title: string; href: string; icon?: "partner" };
  isHrefActive: (href: string) => boolean;
}) {
  const isHashLink = link.href.includes("#");
  const isActive = !isHashLink && isHrefActive(link.href);
  const iconKey = "icon" in link ? link.icon : undefined;
  const Icon = iconKey === "partner" ? Handshake : null;

  return (
    <li>
      <Link
        href={link.href}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "group inline-flex w-fit items-center gap-1.5 whitespace-nowrap border-b border-transparent pb-0.5 transition-colors duration-200 hover:border-foreground/35 hover:text-foreground",
          isActive
            ? "border-foreground/55 text-foreground"
            : "text-muted-foreground",
        )}
      >
        {Icon ? (
          <Icon className="size-3.5 shrink-0 text-muted-foreground transition-colors duration-200 group-hover:text-foreground group-aria-[current=page]:text-foreground" />
        ) : null}
        <span>{link.title}</span>
      </Link>
    </li>
  );
}
